import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzTableModule } from 'ng-zorro-antd/table';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { FlexModule } from '@angular/flex-layout';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { MatIconModule } from '@angular/material/icon';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { TooltipComponent } from 'src/app/common/tooltip/tooltip.component';
import { IVelesMocaAssessment } from 'src/app/interfaces/Veles/IVelesMocaAssessment';
import { VelesMocaAssessmentService } from 'src/app/services/velesMocaAssessment.service';
import { Subject, takeUntil } from 'rxjs';
import { BrowserModule } from '@angular/platform-browser';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import Swal from 'sweetalert2';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MocaAssessmentAddComponent } from './moca-assessment-add/moca-assessment-add.component';
import moment from 'moment';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';
import { datetime_format } from 'src/date/date.configuration';
import { PermissionModule } from 'src/app/modules/permission.module';
import { NzImageModule } from 'ng-zorro-antd/image';
import { ALLOWED_MIME } from 'src/app/guards/file.guard';

@Component({
  selector: 'app-moca-assessment',
  standalone: true,
  templateUrl: './moca-assessment.component.html',
  styleUrls: ['./moca-assessment.component.css'],
  imports: [
    NzLayoutModule,
    NzTableModule,
    TranslateModule,
    CommonModule,
    FlexModule,
    NzTooltipModule,
    NzIconModule,
    MatIconModule,
    TooltipComponent,
    NzSwitchModule,
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzPaginationModule,
    BrowserModule,
    PdfViewerModule,
    NzSpinModule,
    NzImageModule,
    PermissionModule,
  ],
})
export class MocaAssessmentComponent {
  @Input() id = '';
  @Input() readonlyMode = false;
  @Input() isPreview: boolean = false;
  @Input() isLoading?: boolean = false;

  @Input() pagination?: boolean = true;

  public addComp = MocaAssessmentAddComponent;

  private destroy$ = new Subject<void>();

  pdfObj?: string;
  pdfItem?: IVelesMocaAssessment;

  @Input() data?: IVelesMocaAssessment[];

  currentPageIndex: number = 1;
  pageSize: number = 5;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;
  isVisible = false;

  isPreviewLoading?: boolean = false;
  fileType?: string;
  fileName?: string;

  constructor(
    private readonly mocaService: VelesMocaAssessmentService,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
    private readonly swalToastService: SwalToastService,
    private readonly translate: TranslateService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.mocaService.mocaLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    if (!this.data) {
      this.mocaService.mocaDataSub
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: IVelesMocaAssessment[]) => {
          this.data = data;

          const firstWithFile = data.find((item) => !!item.fileName);
          if (firstWithFile) {
            this.requestPDF(firstWithFile);
          }
        });

      this.mocaService.getData();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
  }

  isEOC() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('episodeOfCare')
    );
  }

  isInpatientView() {
    return (
      !(
        this.eocService.currentEocId() === null ||
        this.eocService.currentEocId() === ''
      ) && this.router.url.includes('inPatientView')
    );
  }

  hasUnsubmitted() {
    return this.getData()?.find(
      (d) => d?.['isSubmitted'] !== undefined && !d?.['isSubmitted']
    );
  }

  getData() {
    if (this.pagination) {
      const startIndex = (this.currentPageIndex - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this?.data?.slice(startIndex, endIndex) || [];
    }
    return this?.data;
  }

  handlePageIndexChange(pageIndex: number): void {
    this.currentPageIndex = pageIndex;
  }

  handlePageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPageIndex = 1;
  }

  openModal() {
    this.mocaService.mocaModalSub.next(true);
  }

  deleteFun(id: string) {
    Swal.fire({
      text: this.translate.instant('veles.moca.deleteConfirm'),
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.mocaService.deleteMocaAssessment(id).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'MoCA assessment deleted successfully'
            );
            if (id === this?.pdfItem?.id) {
              if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
              this.pdfObj = undefined;
              this.pdfItem = undefined;
              this.fileType = undefined;
              this.fileName = undefined;
            }

            this.mocaService.deleteMocaAssessmentFromTable(id);
          },
          error: () => {
            this.swalToastService.toastError(
              'Unable to delete MoCA assessment'
            );
          },
        });
      }
    });
  }

  requestPDF(item?: IVelesMocaAssessment) {
    if (!item?.fileName) {
      if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
      this.pdfObj = undefined;
      this.pdfItem = item;
      this.fileType = undefined;
      this.fileName = undefined;
      this.isPreviewLoading = false;
      return;
    }

    this.isPreviewLoading = true;
    this.mocaService.getPDF(item.id).subscribe({
      next: (res: Blob) => {
        if (!ALLOWED_MIME.includes(res.type as any)) {
          this.swalToastService.toastError('Unsupported file type.');
          this.isPreviewLoading = false;
          this.pdfObj = undefined;
          this.pdfItem = undefined;
          this.fileType = undefined;
          this.fileName = undefined;
          return;
        }
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = URL.createObjectURL(res);
        this.fileType = res.type || undefined;
        this.pdfItem = item;
        const filePath = item.path || '';
        const fileNameIndex = filePath.lastIndexOf('/');
        this.fileName =
          fileNameIndex === -1
            ? item.fileName || undefined
            : filePath.substring(fileNameIndex + 1);
        this.isPreviewLoading = false;
      },
      error: () => {
        this.swalToastService.toastError('Unable to retrieve the document');
        if (this.pdfObj) URL.revokeObjectURL(this.pdfObj);
        this.pdfObj = undefined;
        this.pdfItem = undefined;
        this.fileType = undefined;
        this.fileName = undefined;
        this.isPreviewLoading = false;
      },
    });
  }

  downloadPDF(item: IVelesMocaAssessment) {
    this.mocaService.downloadPDF(item);
  }

  getValue(field: string, value?: string) {
    if (!value) {
      return '-';
    }

    if (field?.toLowerCase()?.includes('date time')) {
      return moment(value).format(datetime_format);
    } else {
      return value;
    }
  }

  handleCancel() {
    this.isVisible = false;

    this.modalRef.destroy();
  }

  hasPermission(permission: string[]) {
    return this.permissionService.can(permission);
  }
}
